import type { Asset } from "@amzn/hardware-self-service-api-model";
import type { MyHardwareWireResponse } from "@/services/my-hardware/my-hardware.mapper";
import type { AssetStatus } from "@/services/my-hardware/my-hardware.types";

export const SIM_LATENCY_MS = 300;
/** Mirrors the upstream custody store's p99 so the loading state is honestly exercised. */
export const SLOW_LATENCY_MS = 6500;

export const SCENARIO_IDS = {
  happy: "happy",
  singleAction: "single-action",
  multiAction: "multi-action",
  allStatuses: "all-statuses",
  empty: "empty",
  emptyEligibility: "empty-eligibility",
  failure: "failure",
  slow: "slow",
} as const;

export type ScenarioId = (typeof SCENARIO_IDS)[keyof typeof SCENARIO_IDS];

export const SCENARIO_LABELS: Record<ScenarioId, string> = {
  [SCENARIO_IDS.happy]: "Happy path",
  [SCENARIO_IDS.singleAction]: "One eligible device (1.1)",
  [SCENARIO_IDS.multiAction]: "Several eligible devices (1.2)",
  [SCENARIO_IDS.allStatuses]: "Every status",
  [SCENARIO_IDS.empty]: "No devices (1.1a)",
  [SCENARIO_IDS.emptyEligibility]: "Eligibility unavailable",
  [SCENARIO_IDS.failure]: "Load failure",
  [SCENARIO_IDS.slow]: "Slow response",
};

export interface MyHardwareScenario {
  id: ScenarioId;
  latencyMs: number;
  shouldFail: boolean;
  wire: MyHardwareWireResponse;
  /**
   * Statuses the wire contract cannot produce yet (orders, returns), applied
   * after mapping so the page can be reviewed with them. Keyed by asset id.
   */
  statusOverrides: Record<string, AssetStatus>;
}

const LAPTOP = "LAPTOP";

const simAsset = (suffix: string, model: string, catalogClass: string): Asset => ({
  id: `sim-asset-${suffix}`,
  serialNumber: `SIM0000${suffix}`,
  assetTag: `A000000${suffix.charAt(1)}`,
  make: "Acme",
  model,
  catalogClass,
});

const eligibleLaptop = simAsset("A1", "Notebook 16", LAPTOP);
const futureEligibleLaptop = simAsset("B2", "Notebook 14", LAPTOP);
const assignedMonitor = simAsset("C3", "Display 27", "MONITOR");
const secondEligibleLaptop = simAsset("D4", "Notebook 13", LAPTOP);
const orderedLaptop = simAsset("E5", "Notebook 17", LAPTOP);
const returningLaptop = simAsset("F6", "Notebook 11", LAPTOP);
const pendingReturnLaptop = simAsset("G7", "Notebook 15", LAPTOP);
const overdueLaptop = simAsset("H8", "Notebook 12", LAPTOP);

const happyWire: MyHardwareWireResponse = {
  assets: [eligibleLaptop, futureEligibleLaptop, assignedMonitor],
  lcrEligibility: {
    [eligibleLaptop.id ?? ""]: { isEligible: true },
    [futureEligibleLaptop.id ?? ""]: { isFutureEolEligible: true, nextEligibleDate: "2027-03-01" },
  },
};

const scenario = (
  id: ScenarioId,
  wire: MyHardwareWireResponse,
  overrides: Partial<Omit<MyHardwareScenario, "id" | "wire">> = {}
): MyHardwareScenario => ({
  id,
  wire,
  latencyMs: SIM_LATENCY_MS,
  shouldFail: false,
  statusOverrides: {},
  ...overrides,
});

const SCENARIOS: Record<ScenarioId, MyHardwareScenario> = {
  [SCENARIO_IDS.happy]: scenario(SCENARIO_IDS.happy, happyWire),
  [SCENARIO_IDS.singleAction]: scenario(SCENARIO_IDS.singleAction, {
    assets: [eligibleLaptop, assignedMonitor],
    lcrEligibility: { [eligibleLaptop.id ?? ""]: { isEligible: true } },
  }),
  [SCENARIO_IDS.multiAction]: scenario(SCENARIO_IDS.multiAction, {
    assets: [eligibleLaptop, secondEligibleLaptop, futureEligibleLaptop, assignedMonitor],
    lcrEligibility: {
      [eligibleLaptop.id ?? ""]: { isEligible: true },
      [secondEligibleLaptop.id ?? ""]: { isEligible: true },
      [futureEligibleLaptop.id ?? ""]: { isFutureEolEligible: true, nextEligibleDate: "2027-03-01" },
    },
  }),
  [SCENARIO_IDS.allStatuses]: scenario(
    SCENARIO_IDS.allStatuses,
    {
      assets: [
        overdueLaptop,
        pendingReturnLaptop,
        eligibleLaptop,
        orderedLaptop,
        returningLaptop,
        assignedMonitor,
      ],
      lcrEligibility: { [eligibleLaptop.id ?? ""]: { isEligible: true } },
    },
    {
      statusOverrides: {
        [overdueLaptop.id ?? ""]: "overdueReturn",
        [pendingReturnLaptop.id ?? ""]: "pendingReturn",
        [orderedLaptop.id ?? ""]: "ordered",
        [returningLaptop.id ?? ""]: "returnInProgress",
      },
    }
  ),
  [SCENARIO_IDS.empty]: scenario(SCENARIO_IDS.empty, {
    assets: [],
    lcrEligibility: {},
  }),
  [SCENARIO_IDS.emptyEligibility]: scenario(SCENARIO_IDS.emptyEligibility, {
    assets: [eligibleLaptop, assignedMonitor],
    lcrEligibility: {},
  }),
  [SCENARIO_IDS.failure]: scenario(SCENARIO_IDS.failure, happyWire, { shouldFail: true }),
  [SCENARIO_IDS.slow]: scenario(SCENARIO_IDS.slow, happyWire, { latencyMs: SLOW_LATENCY_MS }),
};

const isScenarioId = (value: string): value is ScenarioId =>
  Object.values(SCENARIO_IDS).includes(value as ScenarioId);

export const resolveScenario = (id: string): MyHardwareScenario =>
  isScenarioId(id) ? SCENARIOS[id] : SCENARIOS[SCENARIO_IDS.happy];
